import { initSentry } from "./lib/sentry.ts";
import { installConsentDefault } from "./lib/consent.ts";
import { initHeadDedupe } from "./lib/headDedupe.ts";

// Install Google Consent Mode v2 defaults BEFORE any tag loads.
installConsentDefault();

// Remove hosting-injected duplicate head tags once Helmet supplies per-route ones.
initHeadDedupe();


import { createRoot } from "react-dom/client";

// Defer Sentry init until idle to avoid blocking first paint.
// Errors thrown synchronously during initial render are still caught by ErrorBoundary;
// Sentry will pick up subsequent errors once initialised.
const deferSentry = () => {
  if (typeof window === "undefined") return;
  const start = () => {
    try { initSentry(); } catch (_e) { /* never block app on monitoring */ }
  };
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(start, { timeout: 4000 });
  } else {
    setTimeout(start, 2000);
  }
};
deferSentry();

import App from "./App.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { initErrorReporting } from "./lib/errorReporting.ts";
import "./index.css";
// react-quill CSS is loaded by the components that need it (admin only)

const LEGACY_CACHE_MARKERS = ["workbox", "precache", "pwa", "vite"];

const clearLegacyServiceWorkers = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const unregisterResults = await Promise.all(
      registrations.map((registration) => registration.unregister())
    );

    let deletedAnyCache = false;

    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      const legacyCacheKeys = cacheKeys.filter((cacheKey) => {
        const normalisedKey = cacheKey.toLowerCase();
        return LEGACY_CACHE_MARKERS.some((marker) => normalisedKey.includes(marker));
      });

      if (legacyCacheKeys.length > 0) {
        const deleteResults = await Promise.all(
          legacyCacheKeys.map((cacheKey) => caches.delete(cacheKey))
        );
        deletedAnyCache = deleteResults.some(Boolean);
      }
    }

    void unregisterResults;
    void deletedAnyCache;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("Failed to clear legacy service worker state", error);
    }
  }
};

void clearLegacyServiceWorkers();

initErrorReporting();

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
